import api from '../lib/api';
import { IAccountDataResponse, IBulkGroupActionResult } from '@tool-no-hu/shared/types';
import { showToast } from '../context/ToastContext';

// ─── Wallet ──────────────────────────────────────────────────────────

/** POST /api/v1/sunwin/accounts/:id/balance */
export async function getSunwinBalanceService(
  id: string,
  silent?: boolean,
): Promise<IAccountDataResponse | null> {
  try {
    const reqConfig: Record<string, any> = {};
    if (silent) reqConfig._skipToast = true;
    const response = await api.post(
      `/api/v1/sunwin/accounts/${id}/balance`,
      {},
      reqConfig as any,
    );
    if (response.data && response.data.code === 200) {
      return response.data.data;
    }
    return null;
  } catch (error: any) {
    console.error('Failed to get account balance:', error);
    return null;
  }
}

/** Một coin trong cấu hình nạp/rút live — network nạp và network rút là 2 tập KHÁC NHAU */
export type CryptoOptionItem = {
  currency: string;
  depositNetworks: string[];
  withdrawalNetworks: string[];
  buyingRate: number;
  sellingRate: number;
};

/**
 * GET /api/v1/sunwin/accounts/:id/crypto-options
 * Đọc live từ paygate — không hard-code danh sách coin/network ở FE vì paygate đổi bất cứ lúc nào.
 */
export async function getSunwinCryptoOptionsService(
  id: string,
): Promise<{ fiatCurrency: string; items: CryptoOptionItem[] } | null> {
  try {
    const response = await api.get(`/api/v1/sunwin/accounts/${id}/crypto-options`);
    if (response.data && response.data.code === 200) {
      return response.data.data;
    }
    return null;
  } catch (error: any) {
    console.error('Failed to get crypto options:', error);
    const msg = error?.response?.data?.message || 'Không lấy được cấu hình nạp/rút';
    showToast.error(msg);
    return null;
  }
}

/** POST /api/v1/sunwin/accounts/:id/withdraw */
export async function sunwinWithdrawService(
  id: string,
  address: string,
  amount: number,
  currency: string,
  network: string,
): Promise<{ pendingTxId: string; currency: string; network: string } | null> {
  try {
    const response = await api.post(`/api/v1/sunwin/accounts/${id}/withdraw`, {
      address,
      amount,
      currency,
      network,
    });
    if (response.data && response.data.code === 200) {
      return response.data.data;
    }
    return null;
  } catch (error: any) {
    console.error('Failed to withdraw:', error);
    const msg = error?.response?.data?.message || 'Rút tiền điện tử thất bại';
    showToast.error(msg);
    return null;
  }
}

/** POST /api/v1/sunwin/accounts/:id/crypto-address */
export async function getSunwinCryptoAddressService(
  id: string,
  currency: string,
  network: string,
): Promise<{ address: string; qrCode: string; network: string } | null> {
  try {
    const response = await api.post(`/api/v1/sunwin/accounts/${id}/crypto-address`, {
      currency,
      network,
    });
    if (response.data && response.data.code === 200) {
      return response.data.data;
    }
    return null;
  } catch (error: any) {
    console.error('Failed to get deposit QR:', error);
    const msg = error?.response?.data?.message || 'Lấy mã nạp thất bại';
    showToast.error(msg);
    return null;
  }
}

/** GET /api/v1/sunwin/accounts/:id/transactions */
export async function getSunwinTransactionHistoryService(
  id: string,
  slipType = 1,
  limit = 20,
  skip = 0,
): Promise<{ count: number; items: any[] } | null> {
  try {
    const response = await api.get(`/api/v1/sunwin/accounts/${id}/transactions`, {
      params: { slipType, limit, skip },
    });
    if (response.data && response.data.code === 200) {
      return response.data.data;
    }
    return null;
  } catch (error: any) {
    console.error('Failed to get transaction history:', error);
    const msg = error?.response?.data?.message || 'Lấy lịch sử giao dịch thất bại';
    showToast.error(msg);
    return null;
  }
}

/** GET /api/v1/sunwin/accounts/:id/betlogs — `count` là tổng, `items` là 1 trang. */
export async function getSunwinBetLogsService(
  id: string,
  limit = 30,
): Promise<{ count: number; items: any[] } | null> {
  try {
    const response = await api.get(`/api/v1/sunwin/accounts/${id}/betlogs`, {
      params: { limit },
    });
    if (response.data && response.data.code === 200) {
      return response.data.data;
    }
    return null;
  } catch (error: any) {
    console.error('Failed to get bet logs:', error);
    const msg = error?.response?.data?.message || 'Lấy lịch sử cược thất bại';
    showToast.error(msg);
    return null;
  }
}

// ─── Sync ────────────────────────────────────────────────────────────

/** POST /api/v1/sunwin/sync/betlog/:accountId */
export async function sunwinSyncBetLogsService(id: string): Promise<any | null> {
  try {
    const response = await api.post(`/api/v1/sunwin/sync/betlog/${id}`);
    if (response.data && response.data.code === 200) {
      return response.data.data;
    }
    return null;
  } catch (error: any) {
    console.error('Failed to sync bet logs:', error);
    const msg = error?.response?.data?.message || 'Đồng bộ lịch sử cược thất bại';
    showToast.error(msg);
    return null;
  }
}

/** POST /api/v1/sunwin/sync/transactions/:accountId */
export async function sunwinSyncTransactionsService(id: string): Promise<any | null> {
  try {
    const response = await api.post(`/api/v1/sunwin/sync/transactions/${id}`);
    if (response.data && response.data.code === 200) {
      return response.data.data;
    }
    return null;
  } catch (error: any) {
    console.error('Failed to sync transactions:', error);
    const msg = error?.response?.data?.message || 'Đồng bộ lịch sử giao dịch thất bại';
    showToast.error(msg);
    return null;
  }
}

// ─── Single account auth ────────────────────────────────────────────

// Các thao tác dưới đây luôn trả HTTP 200 kèm mảng kết quả TỪNG tài khoản, kể
// cả khi mọi tài khoản đều lỗi. Interceptor mặc định lại bắn toast SUCCESS cho
// mọi response 2xx non-GET, nên câu "Đăng nhập nhóm thất bại (tất cả tài khoản
// lỗi)" hiện ra dưới dạng toast XANH và nuốt luôn phần chi tiết. Đánh dấu
// _skipToast để caller tự dựng thông báo từ mảng kết quả.
const ACTION_CFG = { _skipToast: true } as any;


/** POST /api/v1/sunwin/accounts/:id/login */
export async function sunwinLoginAccountService(id: string): Promise<any> {
  try {
    const response = await api.post(`/api/v1/sunwin/accounts/${id}/login`, {}, ACTION_CFG);
    return response.data;
  } catch (error: any) {
    console.error('Failed to login account:', error);
    // Trả lại thân lỗi của server để caller đọc được `message`. Bản cũ nuốt
    // sạch và return null, nên UI chỉ biết "thất bại" mà không biết vì sao.
    return error?.response?.data ?? null;
  }
}

/** POST /api/v1/sunwin/accounts/:id/logout */
export async function sunwinLogoutAccountService(id: string): Promise<any> {
  try {
    const response = await api.post(`/api/v1/sunwin/accounts/${id}/logout`, {}, ACTION_CFG);
    return response.data;
  } catch (error: any) {
    console.error('Failed to logout account:', error);
    // Trả lại thân lỗi của server để caller đọc được `message`. Bản cũ nuốt
    // sạch và return null, nên UI chỉ biết "thất bại" mà không biết vì sao.
    return error?.response?.data ?? null;
  }
}

// ─── Game (Group operations) ─────────────────────────────────────────

/** POST /api/v1/sunwin/groups/:id/login */
export async function sunwinLoginGroupService(id: string): Promise<any> {
  try {
    const response = await api.post(`/api/v1/sunwin/groups/${id}/login`, {}, ACTION_CFG);
    return response.data;
  } catch (error: any) {
    console.error('Failed to login group:', error);
    // Trả lại thân lỗi của server để caller đọc được `message`. Bản cũ nuốt
    // sạch và return null, nên UI chỉ biết "thất bại" mà không biết vì sao.
    return error?.response?.data ?? null;
  }
}

/** POST /api/v1/sunwin/groups/:id/livestream/start */
export async function sunwinStartLivestreamService(id: string): Promise<any> {
  try {
    const response = await api.post(`/api/v1/sunwin/groups/${id}/livestream/start`, {}, ACTION_CFG);
    return response.data;
  } catch (error: any) {
    console.error('Failed to start livestream group:', error);
    // Trả lại thân lỗi của server để caller đọc được `message`. Bản cũ nuốt
    // sạch và return null, nên UI chỉ biết "thất bại" mà không biết vì sao.
    return error?.response?.data ?? null;
  }
}

/** POST /api/v1/sunwin/groups/:id/livestream/stop */
export async function sunwinStopLivestreamService(id: string): Promise<any> {
  try {
    const response = await api.post(`/api/v1/sunwin/groups/${id}/livestream/stop`, {}, ACTION_CFG);
    return response.data;
  } catch (error: any) {
    console.error('Failed to stop livestream group:', error);
    // Trả lại thân lỗi của server để caller đọc được `message`. Bản cũ nuốt
    // sạch và return null, nên UI chỉ biết "thất bại" mà không biết vì sao.
    return error?.response?.data ?? null;
  }
}

/** POST /api/v1/sunwin/groups/:id/livestream/restart */
export async function sunwinRestartLivestreamService(id: string): Promise<any> {
  try {
    const response = await api.post(`/api/v1/sunwin/groups/${id}/livestream/restart`, {}, ACTION_CFG);
    return response.data;
  } catch (error: any) {
    console.error('Failed to restart livestream group:', error);
    // Trả lại thân lỗi của server để caller đọc được `message`. Bản cũ nuốt
    // sạch và return null, nên UI chỉ biết "thất bại" mà không biết vì sao.
    return error?.response?.data ?? null;
  }
}

/** POST /api/v1/sunwin/groups/:id/logout */
export async function sunwinLogoutGroupService(id: string): Promise<any> {
  try {
    const response = await api.post(`/api/v1/sunwin/groups/${id}/logout`, {}, ACTION_CFG);
    return response.data;
  } catch (error: any) {
    console.error('Failed to logout group:', error);
    // Trả lại thân lỗi của server để caller đọc được `message`. Bản cũ nuốt
    // sạch và return null, nên UI chỉ biết "thất bại" mà không biết vì sao.
    return error?.response?.data ?? null;
  }
}

/**
 * POST /api/v1/sunwin/groups/bulk-action — start/stop NHIỀU nhóm một lượt.
 * Trả về mảng kết quả từng nhóm (ok/skipped/error) để widget hiện bảng kết quả.
 * `_skipToast`: bulk tự hiện modal kết quả nên không cần toast của interceptor.
 */
export async function sunwinBulkGroupActionService(
  action: 'start' | 'stop',
  groupIds: string[],
): Promise<IBulkGroupActionResult[]> {
  const response = await api.post(
    '/api/v1/sunwin/groups/bulk-action',
    { action, groupIds },
    ACTION_CFG,
  );
  return response.data?.data?.results ?? [];
}
